/**
 * Voice authorship RPC — optional mark-as-reviewed on Voice tool responses.
 */

import { getSupabaseClient } from '../../lib/supabaseClient';
import type {
  CreationSource,
  CreationReviewStatus,
  CreationProvenance,
} from '../../types/prioritization/creationProvenance';

export interface MarkVoiceResponseReviewedResult {
  success: boolean;
  error?: string;
  response?: {
    id: string;
    creationSource: CreationSource;
    creationReviewStatus: CreationReviewStatus;
    creationProvenance: CreationProvenance;
    reviewedBy?: string;
    reviewedAt?: string;
  };
}

/**
 * Optionally mark a Voice response as human-reviewed.
 * Does not change counting policy. Review is never required.
 */
export async function markVoiceResponseReviewedRpc(
  responseId: string,
  reviewedById: string,
): Promise<MarkVoiceResponseReviewedResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('mark_voice_response_reviewed', {
    p_response_id: responseId,
    p_reviewed_by: reviewedById,
  });

  if (error) return { success: false, error: error.message };

  const result = data as {
    success?: boolean;
    error?: string;
    response_id?: string;
    creation_source?: CreationSource;
    creation_review_status?: CreationReviewStatus;
    creation_provenance?: CreationProvenance;
    reviewed_by?: string;
    reviewed_at?: string;
  } | null;

  if (!result) return { success: false, error: 'No response from server' };
  if (!result.success) return { success: false, error: result.error ?? 'Mark reviewed failed' };

  return {
    success: true,
    response: result.response_id
      ? {
          id: result.response_id,
          creationSource: result.creation_source ?? 'ai',
          creationReviewStatus: result.creation_review_status ?? 'human_reviewed',
          creationProvenance: result.creation_provenance ?? 'ai_approved',
          reviewedBy: result.reviewed_by,
          reviewedAt: result.reviewed_at,
        }
      : undefined,
  };
}
